import { jsx } from 'slate-hyperscript';
import { TEXT_TAGS, ELEMENT_TAGS } from './tags';
import { Descendant, Element as SlateElement } from 'slate';
import { Attributes } from './types';
import { unsupportedType } from '../../unsupported/UnsupportedElement';
import { AulaText } from '../../../types';

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

const QUOTE_REGEX = /MsoQuote/g;
const APPLE_SPACE_REGEX = /Apple-converted-space/g;
const NEWLINE_REGEX = /\n[\s]*(?!\n)/g;
const LIST_REGEX = /MsoListParagraph/g;
const COMMENT_LIST_REGEX = /mso-element:comment-list/g;
const COMMENT_REFERENCE_REGEX = /MsoCommentReference/g;
const COMMENT_REFERENCE_ANCHOR_REGEX = /msocomanchor/g;
const SAFARI_COMMENT_REGEX = /msocomtxt/g;

const listTypes = [
  'numbered-list',
  'bulleted-list',
  'ordered-list',
  'unordered-list',
];

const isSpecificElementStyle = (element: Element | null, ...styles: RegExp[]) =>
  styles.some(
    (style) => !!element?.attributes?.getNamedItem('style')?.value.match(style),
  );

const isSpecificElementType = (element: Element | null, ...types: RegExp[]) =>
  types.some(
    (type) => !!element?.attributes?.getNamedItem('class')?.value.match(type),
  );

const isValidWebLink = (element: Element): boolean => {
  const href = element.attributes.getNamedItem('href');

  if (!href) return false;

  if (href.value.trim()[0] === '#') return false;

  if (href.value.trim().startsWith('file://')) return false;

  if (href.value.trim().startsWith('applewebdata://')) return false;

  return true;
};

const isMsoComment = (element: Element) =>
  isSpecificElementStyle(element, COMMENT_LIST_REGEX) ||
  isSpecificElementType(
    element,
    COMMENT_REFERENCE_REGEX,
    COMMENT_REFERENCE_ANCHOR_REGEX,
    SAFARI_COMMENT_REGEX,
  );

const isMsoListItem = (element: Element) =>
  element.nodeName === 'P' &&
  ((isSpecificElementType(element, LIST_REGEX) &&
    element.childNodes?.length > 1) ||
    element.getAttribute('style')?.includes('mso-list:l'));

const isNonFirstMsoListItem = (node: HTMLElement) => {
  return (
    isMsoListItem(node) &&
    node.previousElementSibling &&
    isMsoListItem(node.previousElementSibling)
  );
};

const isOrderedList = (el: HTMLElement) => {
  const val = el.textContent?.replace(NEWLINE_REGEX, '').trim()?.[0];
  if (!val) return false;
  const regex = /^(\d|\w)+$/;
  return !!regex.test(val);
};

const parseListItem = (el: HTMLElement) => {
  const children = Array.from(el.childNodes)
    .filter((child) => child.nodeName !== '#comment')
    .slice(1);

  const content = children
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    .map((child) => parseNode(child as HTMLElement))
    .flat();

  return jsx('element', { type: 'list-item', className: 'level1' }, content);
};

const getSiblings = (el: Element | null): Element[] => {
  return !!el && isMsoListItem(el)
    ? [el, ...getSiblings(el.nextElementSibling)]
    : [];
};

const wrapChildrenInElementNode = (items: SlateElement[]) =>
  items.map((item) => ({
    ...item,
    children: [
      {
        children: item.children,
      },
    ],
  }));

const parseMsoList = (el: HTMLElement) => {
  const listItems = getSiblings(el);

  const type = isOrderedList(el) ? 'OL' : 'UL';
  const listWrapper = document.createElement(type);

  listItems.forEach((item) => listWrapper.appendChild(item));

  const attrs = ELEMENT_TAGS[type](listWrapper);
  const children: SlateElement[] = Array.from(listWrapper.childNodes)
    .map((child) => parseListItem(child as HTMLElement))
    .flat();

  // valid list items should have exactly 1 child which is an element node;
  // at this point the list item element will have 1 or more text nodes as children,
  // so these text nodes need to be wrapped in a new element node
  return jsx('element', attrs, wrapChildrenInElementNode(children));
};

const parseTextContent = (
  { textContent, parentNode, previousSibling }: Node,
  markAttributes: Attributes,
) => {
  if (!textContent) return jsx('fragment');

  /*
    Before we can remove '\n', Word can add this element either:
    1) between two text nodes like 'Hello','\n','World' instead of 'Hello',' ','World'
    2) before the word in a text node like '\nSomeText' instead of ' SomeText'
  */
  if (parentNode?.nodeName === 'O:P' || previousSibling?.nodeName === 'BR') {
    // Replace '\n' with Fragment
    if (textContent.trim() === '') return jsx('fragment');

    // Replace '\nSomeText' with 'SomeText'
    if (
      ['\n', '\r'].includes(textContent.substring(0, 1)) &&
      textContent.length > 1
    ) {
      return jsx('text', markAttributes, textContent.substring(1));
    }
  }

  // Replace 'SomeText\nSomeOtherText' with 'SomeText SomeOtherText and 'SomeText\n     SomeOtherText' with 'SomeText SomeOtherText
  return jsx('text', markAttributes, textContent.replace(NEWLINE_REGEX, ' '));
};

const getColorAttributesFromElement = (element: HTMLElement): Attributes =>
  // Word app elements can sometimes have "windowtext" color,
  // which is not a valid color, and causes parent colours to be wrongly overridden
  element.style?.color && element.style.color !== 'windowtext'
    ? { c: element.style.color }
    : {};

const getTextTagAttributesFromElement = (element: HTMLElement): Attributes =>
  TEXT_TAGS[element.nodeName] || {};

const parseChildElements = (
  childElements: ChildNode[],
  attributes: Attributes = {},
) =>
  childElements
    // Mso list items don't have a shared parent that represents a list.
    // An Mso list is just a collection of paragraphs with specific class or style that marks them as lists.
    // We are parsing all these paragraphs when parsing the first paragraph in a list,
    // so all list item paragraphs starting from the second one should be filtered out here.
    .reduce(
      (children: Descendant[], childElement: ChildNode) =>
        !isMsoComment(childElement as Element) &&
        !isNonFirstMsoListItem(childElement as HTMLElement)
          ? // eslint-disable-next-line @typescript-eslint/no-use-before-define
            children.concat(parseNode(childElement, attributes))
          : children,
      [],
    );

const parseHtmlElement = (element: HTMLElement, attributes: Attributes) => {
  if (element.nodeName === 'BR') return jsx('text', {}, '\n');

  if (element.nodeName === 'IMG')
    return jsx(
      'element',
      {
        type: unsupportedType,
        variant: 'image',
      },
      [{ text: '' }],
    );

  let parent: HTMLElement | ChildNode = element;

  if (element.nodeName === 'PRE' && element.childNodes[0]?.nodeName === 'CODE')
    parent = element.childNodes[0];

  // Only Happens In Safari
  if (isSpecificElementType(element, APPLE_SPACE_REGEX))
    return jsx('text', {}, ' ');

  const colorAttributes = getColorAttributesFromElement(element);
  const textTagAttributes = getTextTagAttributesFromElement(element);
  const allElementAttributes: Attributes = {
    ...attributes,
    ...colorAttributes,
    ...textTagAttributes,
  };

  const children = parseChildElements(
    Array.from(parent.childNodes),
    allElementAttributes,
  );

  if (ELEMENT_TAGS[element.nodeName]) {
    if (element.nodeName === 'P') {
      if (!children.filter((child) => !!child).length) {
        return jsx('element', { type: 'p', ...colorAttributes }, [
          { text: '' },
        ]);
      }

      const nonEmptyChildren = children.filter(
        (child) =>
          !(child as AulaText).text || (child as AulaText).text.trim() !== '',
      );

      if (
        nonEmptyChildren.length === 1 &&
        SlateElement.isElement(nonEmptyChildren[0]) &&
        nonEmptyChildren[0].type === unsupportedType
      ) {
        return children;
      }
    }

    if (element.nodeName === 'A' && !isValidWebLink(element)) {
      return jsx('fragment', {}, children);
    }

    if (isSpecificElementType(element, QUOTE_REGEX))
      return jsx(
        'element',
        { type: 'blockquote', className: 'blockquote' },
        children,
      );

    const attrs = ELEMENT_TAGS[element.nodeName](element);

    if (listTypes.includes(attrs.type)) {
      // the aula-editor currently doesn't support nested lists, so we are inserting
      // nested list items at the first level
      if (
        element.parentElement &&
        ['OL', 'UL'].includes(element.parentElement.nodeName)
      ) {
        return jsx('fragment', {}, children);
      }

      // valid list items should have exactly 1 child which is an element node;
      // at this point the list item element will have 1 or more text nodes as children,
      // so these text nodes need to be wrapped in a new element node
      return jsx(
        'element',
        { ...attrs, ...colorAttributes },
        wrapChildrenInElementNode(
          (children as SlateElement[]).filter(
            (child) => child.type === 'list-item',
          ),
        ),
      );
    }

    if (isMsoListItem(element)) {
      return parseMsoList(element);
    }

    return jsx('element', { ...attrs, ...colorAttributes }, children);
  }

  return children;
};

const parseNode = (
  node: ChildNode,
  markAttributes: Attributes = {},
): Descendant[] | Descendant => {
  switch (node.nodeType) {
    case TEXT_NODE:
      return parseTextContent(node, markAttributes);
    case ELEMENT_NODE:
      return parseHtmlElement(node as HTMLElement, markAttributes);
    default:
      return jsx('fragment');
  }
};

const convertIsolatedInlineLinksToBlockLinks = (child: Descendant) => {
  if (
    SlateElement.isElement(child) &&
    child.children.length === 1 &&
    SlateElement.isElement(child.children[0]) &&
    child.children[0].type === 'inlineLink' &&
    !!child.children[0].url
  ) {
    return {
      ...child.children[0],
      type: 'blockLink',
    };
  }
  return child;
};

export const parseMsDocHtmlToSlate = (element: HTMLElement): Descendant[] => {
  if (element.nodeName === 'BODY') {
    const childElements = Array.from(element.childNodes).filter(
      (childNode): childNode is HTMLElement =>
        childNode.nodeType === ELEMENT_NODE,
    );

    const children = parseChildElements(childElements);

    const convertedChildren = children.map(
      convertIsolatedInlineLinksToBlockLinks,
    );

    return jsx('fragment', {}, convertedChildren);
  }

  throw Error('Failed to parse HTML, root element was not `BODY`');
};
